import { CompleteCareDialog } from "@/features/care-queue/components/CompleteCareDialog";
import { CareHistory } from "@/features/care-queue/components/CareHistory";
import { getAttendanceOccurrences } from "@/api/attendance";
import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import { invalidateMembershipQueries } from "@/features/memberships/queries";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMinistryContexts,
  getMembershipRoster,
  findMembershipCandidates,
  joinMinistry,
  leaveMinistry,
  type MinistryContext,
  type Membership,
} from "@/api/authorization";
import {
  ministryReport,
  ministryIntelligence,
  ministryCare,
  ministryOpenCareCount,
  createMinistryCare,
  operationError,
} from "@/api/ministryOperations";
import { updateCareQueueItem, type CareQueueStatus, type CareQueueItem } from "@/api/careQueue";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  PageContainer,
  PageHeader,
  DataTable,
  DataTablePagination,
  StatCard,
  LoadingState,
  EmptyState,
} from "@/design-system";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QueryErrorState } from "@/components/ui/QueryErrorState";
import { Card } from "@/components/ui/card";

const input =
  "min-w-0 w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white focus:border-indigo-400";
function localNow() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}
function ErrorMessage({ error }: { error: unknown }) {
  return error ? (
    <p role="alert" className="py-3 text-rose-300">
      {operationError(error)}
    </p>
  ) : null;
}
function Pagination({
  page,
  total,
  onChange,
}: {
  page: number;
  total: number;
  onChange: (page: number) => void;
}) {
  return (
    <div className="space-y-3 pt-4">
      <p className="text-sm text-slate-400">{total} results</p>
      <DataTablePagination
        page={page + 1}
        canGoNext={(page + 1) * 25 < total}
        onPrevious={() => onChange(page - 1)}
        onNext={() => onChange(page + 1)}
      />
    </div>
  );
}
export function YouthChurchPage() {
  const { churchId } = useAuth();
  const contexts = useQuery({
    queryKey: ["ministry-contexts", churchId],
    queryFn: () => getMinistryContexts(churchId!),
    enabled: !!churchId,
  });
  const context = contexts.data?.find((c) => c.is_youth_church);
  return (
    <PageContainer>
      <div className="space-y-6 text-slate-300">
        <PageHeader
          title={context?.name ?? "Youth Ministry"}
          subtitle="Ministries · Attendance, engagement and care in this ministry."
        />
        {contexts.isPending && <LoadingState message="Loading ministry…" />}
        {contexts.isError && (
          <QueryErrorState
            title="Unable to load ministry"
            onRetry={() => void contexts.refetch()}
          />
        )}
        {!contexts.isPending && !contexts.error && !context && (
          <Card className="p-6">
            Youth Ministry is not configured or you do not have ministry access.
            A church administrator can configure the ministry and assign scoped
            permissions in Administration.
          </Card>
        )}
        {context && churchId && (
          <YouthOperations
            key={`${churchId}:${context.ministry_id}`}
            church={churchId}
            context={context}
          />
        )}
      </div>
    </PageContainer>
  );
}
function YouthOperations({
  church,
  context,
}: {
  church: string;
  context: MinistryContext;
}) {
  const [params, setParams] = useSearchParams();
  const panels = [
    { id: "overview", label: "Overview", permission: "" },
    { id: "roster", label: "Roster", permission: "MEMBERS_VIEW" },
    { id: "attendance", label: "Attendance", permission: "ATTENDANCE_VIEW" },
    { id: "reports", label: "Attendance report", permission: "REPORTS_VIEW" },
    {
      id: "intelligence",
      label: "Intelligence",
      permission: "INTELLIGENCE_VIEW",
    },
    { id: "care", label: "Care / Follow-up", permission: "CARE_VIEW" },
  ].filter((p) => !p.permission || context.permissions.includes(p.permission));
  const tab =
    panels.find((p) => p.id === params.get("tab"))?.id ?? panels[0]?.id;
  return (
    <>
      <div className="rounded-2xl border border-indigo-500/20 bg-indigo-500/5 p-4">
        <Badge variant="info">{context.name}</Badge>
        <p className="mt-2 text-sm text-slate-400">
          Attendee membership controls service eligibility. Leadership roles do
          not enroll attendees. Ministry attendance remains separate from Main
          Church attendance.
        </p>
      </div>
      <nav
        aria-label={`${context.name} navigation`}
        className="flex flex-wrap gap-2 rounded-2xl border border-slate-800 bg-slate-900/60 p-2"
      >
        {panels.map((p) => (
          <Button
            key={p.id}
            size="sm"
            variant={tab === p.id ? "primary" : "secondary"}
            aria-current={tab === p.id ? "page" : undefined}
            onClick={() => setParams({ tab: p.id })}
          >
            {p.label}
          </Button>
        ))}
        {context.permissions.includes("SERVICES_VIEW") && (
          <Link
            className="rounded-xl px-3 py-2 text-sm font-medium text-indigo-300 hover:bg-slate-800"
            to={`/services?ministry_id=${context.ministry_id}`}
          >
            Services & check-in
          </Link>
        )}
      </nav>
      {tab === "overview" && (
        <YouthOverview church={church} context={context} />
      )}
      {tab === "roster" && <YouthRoster church={church} context={context} />}
      {tab === "attendance" && <MinistryAttendance church={church} context={context} />}
      {tab === "reports" && <YouthReport church={church} context={context} />}
      {tab === "intelligence" && (
        <YouthIntelligence church={church} context={context} />
      )}
      {tab === "care" && <YouthCare church={church} context={context} />}
    </>
  );
}
function YouthRoster({
  church,
  context,
}: {
  church: string;
  context: MinistryContext;
}) {
  const client = useQueryClient();
  const [history, setHistory] = useState(false);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [member, setMember] = useState("");
  const [effective, setEffective] = useState(localNow);
  const [leaving, setLeaving] = useState<Membership | null>(null);
  const manage = context.permissions.includes("MEMBERS_MANAGE");
  const { permissions: churchPermissions } = useEffectivePermissions();
  const canDiscover = manage && churchPermissions.has("MEMBERS_VIEW");
  const roster = useQuery({
    queryKey: ["youth-roster", church, context.ministry_id, history, page],
    queryFn: () =>
      getMembershipRoster(church, context.ministry_id, history, page * 25),
  });
  const candidates = useQuery({
    queryKey: ["youth-candidates", church, context.ministry_id, search],
    queryFn: () =>
      findMembershipCandidates(church, context.ministry_id, search),
    enabled: canDiscover && search.trim().length >= 2,
  });
  const mutation = useMutation({
    mutationFn: () =>
      leaving
        ? leaveMinistry(
            church,
            context.ministry_id,
            leaving.id,
            new Date(effective).toISOString(),
          )
        : joinMinistry(
            church,
            context.ministry_id,
            member,
            new Date(effective).toISOString(),
          ),
    onSuccess: async () => {
      setLeaving(null);
      setMember("");
      setSearch("");
      await invalidateMembershipQueries(client, church);
    },
  });
  return (
    <Card className="space-y-4 p-4 sm:p-6">
      <h2 className="text-xl font-semibold text-white">Attendee roster</h2>
      <label className="flex gap-2">
        <input
          type="checkbox"
          checked={history}
          onChange={(e) => {
            setHistory(e.target.checked);
            setPage(0);
          }}
        />
        Include past membership periods
      </label>
      {manage && (canDiscover || leaving || member) && (
        <form
          className="flex flex-wrap items-end gap-3 rounded-xl bg-slate-900 p-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          {leaving ? (
            <div>
              <p>
                End membership for {leaving.first_name} {leaving.last_name}?
              </p>
              <p className="text-sm">
                Past attendance is retained. Ministry eligibility ends at this
                time.
              </p>
            </div>
          ) : (
            <>
              <label className="grid min-w-0 flex-1 basis-48 gap-2 text-sm">
                Find an existing member
                <input
                  className={input}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setMember("");
                  }}
                  disabled={!canDiscover}
                  placeholder={canDiscover ? "At least 2 characters" : "Rejoining selected member"}
                />
              </label>
              <label className="grid min-w-0 flex-1 basis-48 gap-2 text-sm">
                Member
                <select
                  required
                  className={input}
                  value={member}
                  onChange={(e) => setMember(e.target.value)}
                >
                  <option value="">Select member</option>
                  {!canDiscover && member && <option value={member}>{roster.data?.items.find(item => item.member_id === member)?.first_name} {roster.data?.items.find(item => item.member_id === member)?.last_name}</option>}
                  {candidates.data?.map((m) => (
                    <option value={m.id} key={m.id}>
                      {m.first_name} {m.last_name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
          <label className="grid min-w-0 flex-1 basis-48 gap-2 text-sm">
            {leaving ? "Leave" : "Join / rejoin"} effective time
            <input
              required
              type="datetime-local"
              className={input}
              value={effective}
              onChange={(e) => setEffective(e.target.value)}
            />
          </label>
          <Button disabled={mutation.isPending || (!leaving && !member)}>
            {mutation.isPending
              ? "Saving…"
              : leaving
                ? "Confirm leave"
                : "Enroll attendee"}
          </Button>
          {leaving && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setLeaving(null)}
            >
              Cancel
            </Button>
          )}
          <ErrorMessage error={mutation.error ?? candidates.error} />
        </form>
      )}
      {roster.isPending && <LoadingState message="Loading roster…" />}
      {roster.isError && (
        <QueryErrorState
          title="Unable to load roster"
          onRetry={() => void roster.refetch()}
        />
      )}
      {roster.data && (
        <>
          <p>
            {roster.data.total}{" "}
            {history ? "membership periods" : "current attendees"}
          </p>
          <DataTable
            data={roster.data.items}
            emptyTitle="No attendee memberships"
            emptyDescription="Current attendees appear here. Include past membership periods to review history."
            columns={[
              {
                key: "member",
                header: "Member / household",
                render: (m) => (
                  <div className="min-w-40">
                    <p>
                      {m.first_name} {m.last_name}
                    </p>
                    <p className="text-sm text-slate-500">
                      {m.household_name ?? "No household"}
                    </p>
                  </div>
                ),
              },
              {
                key: "joined",
                header: "Joined",
                render: (m) => new Date(m.joined_at).toLocaleString(),
              },
              {
                key: "left",
                header: "Left",
                render: (m) =>
                  m.left_at ? (
                    new Date(m.left_at).toLocaleString()
                  ) : (
                    <Badge variant="success">Active</Badge>
                  ),
              },
              {
                key: "action",
                header: "Action",
                render: (m) =>
                  manage && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        mutation.reset();
                        setEffective(localNow());
                        if (m.left_at) {
                          setLeaving(null);
                          setSearch(m.last_name);
                          setMember(m.member_id);
                        } else {
                          setLeaving(m);
                        }
                      }}
                    >
                      {m.left_at ? "Rejoin" : "Leave"}
                    </Button>
                  ),
              },
            ]}
          />
          <Pagination
            page={page}
            total={roster.data.total}
            onChange={setPage}
          />
        </>
      )}
    </Card>
  );
}
function YouthReport({
  church,
  context,
}: {
  church: string;
  context: MinistryContext;
}) {
  const [days, setDays] = useState(90);
  const report = useQuery({
    queryKey: ["youth-report", church, context.ministry_id, days],
    queryFn: () => ministryReport(church, context.ministry_id, days),
  });
  return (
    <Card className="space-y-4 p-4 sm:p-6">
      <h2 className="text-xl font-semibold text-white">
        Attendance & visitors
      </h2>
      <label className="flex max-w-xs items-center gap-3 text-sm">
        Period{" "}
        <select
          className={input}
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {[30, 90, 180, 365].map((d) => (
            <option key={d} value={d}>
              {d} days
            </option>
          ))}
        </select>
      </label>
      <p className="text-sm">
        The rate uses eligible member-occurrence pairs within this period,
        including historical membership. Visitors are counted separately and
        remain in their original visits after conversion. Future and cancelled
        services are excluded.
      </p>
      {report.isPending && <LoadingState message="Loading report…" />}
      {report.isError && (
        <QueryErrorState
          title="Unable to load report"
          onRetry={() => void report.refetch()}
        />
      )}
      {report.data && (
        <>
          <div className="grid gap-4 xl:grid-cols-3">
            <Stat
              label="Member attendance"
              value={report.data.present_members}
            />
            <Stat label="Visitor attendance" value={report.data.visitors} />
            <Stat
              label="Attendance rate"
              value={
                report.data.attendance_rate === null
                  ? "No eligible services"
                  : `${report.data.attendance_rate}%`
              }
            />
          </div>
          <DataTable
            data={report.data.occurrences}
            emptyTitle="No services in this period"
            emptyDescription="Choose a longer period or return after a service has taken place."
            columns={[
              {
                key: "service",
                header: "Service",
                render: (o) =>
                  context.permissions.includes("ATTENDANCE_VIEW") ? (
                    <Link
                      className="text-indigo-300"
                      to={`/attendance/${o.occurrence_id}`}
                    >
                      {new Date(o.starts_at).toLocaleString()} · {o.name}
                    </Link>
                  ) : (
                    <span>
                      {new Date(o.starts_at).toLocaleString()} · {o.name}
                    </span>
                  ),
              },
              {
                key: "eligible",
                header: "Eligible",
                render: (o) => o.eligible_members,
              },
              {
                key: "present",
                header: "Present",
                render: (o) => o.present_members,
              },
              {
                key: "visitors",
                header: "Visitors",
                render: (o) => o.visitors,
              },
            ]}
          />
        </>
      )}
    </Card>
  );
}
function Stat({ label, value }: { label: string; value: string | number }) {
  return <StatCard title={label} value={value} />;
}
function YouthIntelligence({
  church,
  context,
}: {
  church: string;
  context: MinistryContext;
}) {
  const [page, setPage] = useState(0);
  const insights = useQuery({
    queryKey: ["youth-intelligence", church, context.ministry_id, page],
    queryFn: () => ministryIntelligence(church, context.ministry_id, page * 25),
  });
  return (
    <Card className="space-y-4 p-4 sm:p-6">
      <h2 className="text-xl font-semibold text-white">Engagement</h2>
      <p>
        Current attendees, evaluated using their eligible ministry occurrences.
        Rates cover 90 days. Membership gaps, future services, and cancellations
        are excluded.
      </p>
      {insights.isPending && <LoadingState message="Loading engagement…" />}
      {insights.isError && (
        <QueryErrorState
          title="Unable to load engagement"
          onRetry={() => void insights.refetch()}
        />
      )}
      {insights.data && (
        <>
          {insights.data.items.map((i) => (
            <div
              key={i.member_id}
              className="min-w-0 break-words rounded-xl border border-slate-800 p-4"
            >
              <h3 className="font-semibold text-white">
                {i.name} · {i.level.replaceAll("_", " ")}{i.score !== null && ` (${i.score}/100)`}
              </h3>
              <p>
                {i.score === null ? "Awaiting assessment" : <>{i.attended_occurrences} of {i.eligible_occurrences} eligible
                services · {i.consecutive_absences} consecutive missed · Last
                attended{" "}
                {i.last_attended_at
                  ? new Date(i.last_attended_at).toLocaleDateString()
                  : "not recorded"}</>}
              </p>
              <p className="mt-2 text-sm text-slate-400">
                {i.explanation.join(" ")}
              </p>
            </div>
          ))}
          {!insights.data.items.length && (
            <EmptyState
              title="No active attendees to assess"
              description="Engagement appears here when there are current attendees."
            />
          )}
          <Pagination
            page={page}
            total={insights.data.total}
            onChange={setPage}
          />
        </>
      )}
    </Card>
  );
}
export function YouthCare({
  church,
  context,
}: {
  church: string;
  context: MinistryContext;
}) {
  const [completing, setCompleting] = useState<CareQueueItem | null>(null);
  const [page, setPage] = useState(0);
  const [title, setTitle] = useState("");
  const [member, setMember] = useState("");
  const [rosterPage, setRosterPage] = useState(0);
  const client = useQueryClient();
  const manage = context.permissions.includes("CARE_MANAGE");
  const care = useQuery({
    queryKey: ["youth-care", church, context.ministry_id, page],
    queryFn: () => ministryCare(church, context.ministry_id, page + 1),
  });
  const roster = useQuery({
    queryKey: ["youth-roster", church, context.ministry_id, false, rosterPage],
    queryFn: () =>
      getMembershipRoster(church, context.ministry_id, false, rosterPage * 25),
    enabled: manage && context.permissions.includes("MEMBERS_VIEW"),
  });
  const mutation = useMutation({
    mutationFn: ({ id, status, completion_comment }: { id: string; status: CareQueueStatus; completion_comment?: string }) =>
      updateCareQueueItem(church, id, { status, completion_comment }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["youth-care"] });
    },
  });
  const create = useMutation({
    mutationFn: () =>
      createMinistryCare(church, context.ministry_id, member, title),
    onSuccess: async () => {
      setTitle("");
      setMember("");
      await client.invalidateQueries({ queryKey: ["youth-care"] });
    },
  });
  return (
    <Card className="space-y-4 p-4 sm:p-6">
      {completing && <CompleteCareDialog item={completing} contextName={context.name} onClose={() => setCompleting(null)}
        onComplete={completion_comment => mutation.mutateAsync({ id: completing.id, status: "COMPLETED", completion_comment })} />}
      <h2 className="text-xl font-semibold text-white">Care / Follow-up</h2>
      <p>
        Automatic attendance concerns are reassessed on return and by the hourly
        sweep. Manual and unrelated concerns stay open until a leader updates
        them.
      </p>
      {manage && roster.data && (
        <form
          className="flex flex-wrap gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <select
            aria-label="Member for follow-up"
            required
            className={input}
            value={member}
            onChange={(e) => setMember(e.target.value)}
          >
            <option value="">Choose attendee</option>
            {roster.data.items.map((m) => (
              <option key={m.id} value={m.member_id}>
                {m.first_name} {m.last_name}
              </option>
            ))}
          </select>
          <input
            aria-label="Follow-up title"
            required
            maxLength={255}
            className={input}
            placeholder="Manual follow-up title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Button disabled={create.isPending}>
            {create.isPending ? "Saving…" : "Add follow-up"}
          </Button>
          <div className="w-full">
            <Pagination
              page={rosterPage}
              total={roster.data.total}
              onChange={(p) => {
                setRosterPage(p);
                setMember("");
              }}
            />
          </div>
        </form>
      )}
      {roster.isError && (
        <QueryErrorState
          title="Unable to load attendees"
          onRetry={() => void roster.refetch()}
        />
      )}{" "}
      {care.isError && (
        <QueryErrorState
          title="Unable to load care"
          onRetry={() => void care.refetch()}
        />
      )}{" "}
      {care.isPending && <LoadingState message="Loading care…" />}
      <ErrorMessage error={mutation.error ?? create.error} />
      {care.data && (
        <>
          {care.data.items.map((item) => (
            <div
              key={item.id}
              className="min-w-0 break-words rounded-xl border border-slate-800 p-4"
            >
              <h3 className="font-semibold text-white">{item.title}</h3>
              <p>{item.member_name || item.household_name}</p>
              <p>{item.description}</p>
              <CareHistory item={item} />
              <p className="py-2 text-sm">{item.recommended_action}</p>
              {manage ? (
                <select
                  aria-label={`Status for ${item.title}`}
                  className={input}
                  value={item.status}
                  disabled={mutation.isPending}
                  onChange={(e) =>
                    e.target.value === "COMPLETED" ? setCompleting(item) : mutation.mutate({
                      id: item.id,
                      status: e.target.value as CareQueueStatus,
                    })
                  }
                >
                  {["OPEN", "IN_PROGRESS", "COMPLETED", "DISMISSED"].map(
                    (s) => (
                      <option key={s} value={s}>
                        {s.replaceAll("_", " ")}
                      </option>
                    ),
                  )}
                </select>
              ) : (
                <p>{item.status}</p>
              )}
            </div>
          ))}
          {!care.data.items.length && (
            <EmptyState
              title="No follow-ups"
              description="Ministry care concerns and manual follow-ups appear here."
            />
          )}
          <Pagination page={page} total={care.data.total} onChange={setPage} />
        </>
      )}
    </Card>
  );
}

function YouthOverview({
  church,
  context,
}: {
  church: string;
  context: MinistryContext;
}) {
  const openCare = useQuery({
    queryKey: ["youth-care", church, context.ministry_id, "open-count"],
    queryFn: () => ministryOpenCareCount(church, context.ministry_id),
    enabled: context.permissions.includes("CARE_VIEW"),
  });
  const roster = useQuery({
    queryKey: ["youth-roster", church, context.ministry_id, false, 0],
    queryFn: () => getMembershipRoster(church, context.ministry_id, false, 0),
    enabled: context.permissions.includes("MEMBERS_VIEW"),
  });
  const report = useQuery({
    queryKey: ["youth-report", church, context.ministry_id, 90],
    queryFn: () => ministryReport(church, context.ministry_id, 90),
    enabled: context.permissions.includes("REPORTS_VIEW"),
  });
  return (
    <Card className="space-y-4 p-4 sm:p-6">
      <h2 className="text-xl font-semibold text-white">Ministry overview</h2>
      <p>
        Use Roster to enroll or end attendee membership. Open Services to select
        an occurrence and record attendance from its eligible roster.
      </p>
      <ErrorMessage error={roster.error ?? report.error ?? openCare.error} />
      {(roster.isLoading || report.isLoading || openCare.isLoading) && (
        <LoadingState message="Loading ministry overview…" />
      )}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {openCare.data !== undefined && (
          <Stat label="Open follow-ups" value={openCare.data} />
        )}{" "}
        {roster.data && (
          <Stat label="Current attendees" value={roster.data.total} />
        )}
        {report.data && (
          <>
            <Stat
              label="Latest service members (90 days)"
              value={
                report.data.occurrences[0]?.present_members ?? "No services"
              }
            />
            <Stat
              label="Latest service visitors (90 days)"
              value={report.data.occurrences[0]?.visitors ?? "No services"}
            />
          </>
        )}
      </div>
      <p className="text-sm text-slate-400">
        Available sections reflect your ministry permissions. Ministry
        attendance is separate from Main Church attendance. Adult leaders remain
        Main Church attendees unless separately enrolled.
      </p>
    </Card>
  );
}

function MinistryAttendance({ church, context }: { church: string; context: MinistryContext }) {
  const query = useQuery({ queryKey: ["attendance-occurrences", church, context.ministry_id], queryFn: () => getAttendanceOccurrences(church, context.ministry_id) });
  if (query.isPending) return <LoadingState message="Loading attendance services…" />;
  if (query.isError) return <QueryErrorState title="Unable to load attendance services" onRetry={() => void query.refetch()} />;
  return <Card className="space-y-3 p-5"><h2 className="text-xl font-semibold">Attendance services</h2>
    {query.data.length ? query.data.map(service => <Link key={service.id} to={`/attendance/${service.id}`} className="block rounded-lg border border-slate-700 p-3 hover:bg-slate-800">
      {service.title} · {new Date(service.starts_at).toLocaleString()}
    </Link>) : <p>No services are available in this ministry.</p>}
  </Card>;
}
